import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, ChevronDown, ChevronUp, Brain, Globe, Film, Share2, BookOpen, Palette, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getServiceData } from '../data/services';

const iconMap = { Brain, Globe, Film, Share2, BookOpen, Palette, Play };

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState('overview');
  const [openFaq, setOpenFaq] = useState(null);

  // Map URL slugs to data keys
  const serviceKeyMap = {
    'ai-systems': 'aiSystems',
    'web-development': 'webDevelopment',
    'content-production': 'contentProduction',
    'social-media': 'socialMedia',
    'publishing-systems': 'publishing',
    'brand-identity': 'brandIdentity',
    'tiktok-management': 'tiktokManagement'
  };

  const dataKey = serviceKeyMap[serviceId];
  const service = getServiceData(language, dataKey);

  const sections = [
    { id: 'overview', label: language === 'en' ? 'Overview' : 'Genel Bakış' },
    { id: 'capabilities', label: language === 'en' ? 'What We Build' : 'Ne İnşa Ediyoruz' },
    { id: 'usecases', label: language === 'en' ? 'Use Cases' : 'Kullanım Alanları' },
    { id: 'process', label: language === 'en' ? 'Process' : 'Sürec' },
    { id: 'deliverables', label: language === 'en' ? 'Deliverables' : 'Teslimatlar' },
    { id: 'faq', label: language === 'en' ? 'FAQ' : 'SSS' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen bg-transparent relative z-10 pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Service not found</h1>
          <Link to="/services" className="text-[#00FFD1]">← Back to Services</Link>
        </div>
      </div>
    );
  }

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-transparent relative z-10 pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            <span>{language === 'en' ? 'All Services' : 'Tüm Hizmetler'}</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-end">
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-8 h-[1px] bg-[#00FFD1]" />
                <span className="text-[#00FFD1] text-xs font-semibold tracking-[0.3em] uppercase">
                  {service.tagline}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] tracking-[-0.02em] mb-6">
                {service.title}
              </h1>
              <p className="text-xl text-white/50 leading-relaxed">
                {service.heroDescription}
              </p>
            </div>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="flex-1 lg:flex-none inline-flex items-center justify-center gap-3 bg-[#00FFD1] text-black px-8 py-4 text-base font-semibold hover:bg-white transition-colors"
              >
                <span>{language === 'en' ? 'Start Project' : 'Proje Başlat'}</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <div className="sticky top-20 lg:top-24 z-40 bg-black/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex overflow-x-auto gap-8 py-4 scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`whitespace-nowrap text-sm font-medium transition-colors ${
                  activeSection === section.id ? 'text-[#00FFD1]' : 'text-white/40 hover:text-white/60'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-24">
            {/* Overview */}
            <section id="overview">
              <h2 className="text-2xl font-semibold text-white mb-6">{service.overview.title}</h2>
              <p className="text-lg text-white/60 leading-relaxed">{service.overview.content}</p>
              
              {service.problemsSolved && (
                <div className="mt-12 bg-[#0A0A0A] border border-white/5 p-8">
                  <h3 className="text-lg font-semibold text-white mb-6">{service.problemsSolved.title}</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.problemsSolved.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check size={16} className="text-[#00FFD1] mt-1 flex-shrink-0" />
                        <span className="text-white/50 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Capabilities */}
            <section id="capabilities">
              <h2 className="text-2xl font-semibold text-white mb-8">{service.whatWeDo.title}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {service.whatWeDo.items.map((item, i) => (
                  <div key={i} className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#00FFD1]/20 transition-colors">
                    <h3 className="text-white font-medium mb-2">{item.title}</h3>
                    <p className="text-white/40 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Use Cases */}
            <section id="usecases">
              <h2 className="text-2xl font-semibold text-white mb-8">{service.useCases.title}</h2>
              <div className="space-y-4">
                {service.useCases.items.map((item, i) => (
                  <div key={i} className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#00FFD1]/20 transition-colors">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-white font-medium">{item.title}</h3>
                      <span className="text-[#00FFD1]/60 text-xs bg-[#00FFD1]/10 px-2 py-1 whitespace-nowrap">
                        {item.industry}
                      </span>
                    </div>
                    <p className="text-white/40 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Process */}
            <section id="process">
              <h2 className="text-2xl font-semibold text-white mb-8">{service.process.title}</h2>
              <div className="space-y-6">
                {service.process.steps.map((step, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <span className="text-[#00FFD1] text-sm font-mono">{step.number}</span>
                    </div>
                    <div className="flex-1 pb-6 border-b border-white/5">
                      <h3 className="text-white font-medium mb-2">{step.title}</h3>
                      <p className="text-white/40 text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Deliverables */}
            <section id="deliverables">
              <h2 className="text-2xl font-semibold text-white mb-8">{service.deliverables.title}</h2>
              <div className="bg-[#0A0A0A] border border-white/5 p-8">
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.deliverables.items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                      <span className="text-white/60 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {service.addOns && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-white mb-4">{service.addOns.title}</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.addOns.items.map((item, i) => (
                      <span key={i} className="text-sm text-white/40 bg-white/5 px-4 py-2">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* FAQ */}
            <section id="faq">
              <h2 className="text-2xl font-semibold text-white mb-8">
                {language === 'en' ? 'Frequently Asked Questions' : 'Sıkça Sorulan Sorular'}
              </h2>
              <div className="space-y-3">
                {service.faq.map((item, i) => (
                  <div key={i} className="bg-[#0A0A0A] border border-white/5">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-6 text-left"
                    >
                      <span className="text-white font-medium pr-4">{item.question}</span>
                      {openFaq === i ? (
                        <ChevronUp size={20} className="text-[#00FFD1] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-white/40 flex-shrink-0" />
                      )}
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-96' : 'max-h-0'}`}>
                      <p className="px-6 pb-6 text-white/50 text-sm leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-40">
              <div className="bg-[#0A0A0A] border border-white/5 p-8">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {language === 'en' ? 'Ready to Start?' : 'Başlamaya Hazır mısınız?'}
                </h3>
                <p className="text-white/40 text-sm mb-6">
                  {language === 'en' 
                    ? 'Book a strategy call to discuss your requirements and explore how we can help.'
                    : 'Gereksinimlerinizi tartışmak için bir strateji görüşmesi rezervasyonu yapın.'}
                </p>
                <Link
                  to="/contact"
                  className="block w-full bg-[#00FFD1] text-black py-4 text-center font-semibold hover:bg-white transition-colors"
                >
                  {language === 'en' ? 'Book Strategy Call' : 'Strateji Görüşmesi'}
                </Link>
              </div>

              <div className="mt-6 bg-[#0A0A0A] border border-white/5 p-8">
                <h3 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider">
                  {language === 'en' ? 'Other Services' : 'Diğer Hizmetler'}
                </h3>
                <div className="space-y-3">
                  {['ai-systems', 'web-development', 'content-production', 'social-media', 'brand-identity', 'tiktok-management']
                    .filter(id => id !== serviceId)
                    .slice(0, 4)
                    .map((id) => (
                      <Link
                        key={id}
                        to={`/services/${id}`}
                        className="block text-white/40 text-sm hover:text-white transition-colors"
                      >
                        {id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
